import { createFileRoute } from "@tanstack/react-router";
import {
  ColumnFiltersState,
  Row,
  createColumnHelper,
} from "@tanstack/react-table";
import {
  getControlName,
  pluralize,
  useAuthConfig,
  useEndpointTagsQuery,
  useSessionStorage,
} from "@theprelude/core";
import { Button, TagIcon, TrashcanIcon } from "@theprelude/ds";
import { useMemo } from "react";
import { z } from "zod";
import Configurations from "@/components/activity/configurations";
import FiltersSection from "@/components/activity/filters-section";
import Recommendation from "@/components/activity/recommendation";
import { setPageTitle } from "@/components/app-shell/app-shell";
import {
  DeleteEndpointModal,
  useDeleteEndpointModal,
} from "@/components/modal/delete-endpoint-modal";
import {
  checkboxColumn,
  platformColumn,
  edrColumn,
  hostColumn,
  lastSeenColumn,
  osColumn,
  policyColumn,
  protectedColumn,
  tagsColumn,
} from "@/components/table/columns";
import { useTable } from "@/components/table/hooks";
import SelectionBar from "@/components/table/selection-bar";
import { VirtualTable } from "@/components/table/virtual-table";
import TagOverlay, {
  useTagOverlayProps,
} from "@/components/tag-overlay/tag-overlay";
import { ensureAuth } from "@/lib/auth";
import { authorizeFeatures } from "@/lib/authorize-feature";
import {
  activityQuerySchema,
  getActivityQuery,
  isPresent,
  isValidControl,
} from "@/lib/validators";
import { Host, endpointsQuery } from "@/queries/endpoints";
const activitySchema = z.object({
  activity: activityQuerySchema.optional(),
  show_table: z
    .optional(z.union([z.literal("threats"), z.literal("tests")]))
    .catch(undefined),
});

export const Route = createFileRoute("/_app/activity")({
  beforeLoad: () => setPageTitle("Activity"),
  loaderDeps: ({ search }) => {
    return { query: getActivityQuery(search.activity) };
  },
  validateSearch: activitySchema,
  loader: async (opts) => {
    await authorizeFeatures(opts.context, ["detect"]);
    return endpointsQuery(
      await ensureAuth(opts.context.auth),
      opts.deps.query,
    ).ensureQuery(opts.context.queryClient);
  },
  component: ActivityPage,
});

function ActivityPage() {
  const { query } = Route.useLoaderDeps();
  const search = Route.useSearch();
  return (
    <div className="flex flex-1 flex-col gap-4 pb-4">
      <FiltersSection
        showTable={search.show_table}
        query={query}
        activity={search.activity ?? {}}
      />
      <Configurations query={query} activity={search.activity ?? {}} />
      <RecommendationView />
      <TableDataView />
    </div>
  );
}

function TableDataView() {
  const authConfig = useAuthConfig();
  const { query } = Route.useLoaderDeps();
  const queryInfo = endpointsQuery(authConfig, query).useQuery();
  const endpoints =
    queryInfo.data?.filter((e) => isPresent(e.performance)) ?? [];

  return <TableView data={endpoints} isLoading={queryInfo.isPending} />;
}

const columnHelper = createColumnHelper<Host>();
const columns = [
  columnHelper.display(checkboxColumn),
  columnHelper.accessor("host", hostColumn),
  columnHelper.accessor((row) => row.dos ?? undefined, platformColumn),
  columnHelper.accessor("os", osColumn),
  columnHelper.accessor(
    (row) =>
      isValidControl(row.control) ? getControlName(row.control) : undefined,
    edrColumn,
  ),
  columnHelper.accessor("last_seen", lastSeenColumn),
  columnHelper.accessor("tags", tagsColumn),
  columnHelper.accessor("policy", policyColumn),
  columnHelper.accessor("performance", protectedColumn),
];

const useColumnFilters = () => {
  const { activity } = Route.useSearch();

  return useMemo(() => {
    const colFilters: ColumnFiltersState = [];

    if (activity?.os) {
      colFilters.push({
        id: "os",
        value: activity.os,
      });
    }

    if (activity?.policy) {
      colFilters.push({
        id: "policy",
        value: activity.policy,
      });
    }

    if (activity?.control !== undefined) {
      colFilters.push({
        id: "control",
        value: isValidControl(activity.control)
          ? getControlName(activity.control)
          : undefined,
      });
    }

    if (activity?.dos) {
      colFilters.push({
        id: "dos",
        value: activity.dos,
      });
    }

    return colFilters;
  }, [activity]);
};

function TableView({ data, isLoading }: { data: Host[]; isLoading: boolean }) {
  const columnFilters = useColumnFilters();
  const table = useTable({
    name: "activity-endpoints",
    data,
    columns,
    state: { columnFilters },
    initialState: {
      sorting: [{ id: "last_seen", desc: true }],
    },
    getRowId: (row) => row.endpoint_id,
  });

  const selectedRows = table.getSelectedRowModel().rows;
  const showSelectionBar = selectedRows.length > 0;

  const rowCount = table.getFilteredRowModel().flatRows.length;

  return (
    <div className="flex flex-1 flex-col gap-4 overflow-hidden px-4">
      <p className="text-secondary-foreground">
        {rowCount} {pluralize(rowCount, "endpoint")}
      </p>

      <VirtualTable
        className="max-h-[80vh]"
        table={table}
        loading={isLoading}
        withActionBar
        emptyState={
          <span className="flex justify-center text-center text-sm text-secondary-foreground">
            There are no results to display at this time. Please add a probe,
            schedule more tests or check back later.
          </span>
        }
      />

      {showSelectionBar && (
        <SelectionBar>
          <Button intent="secondary" onClick={() => table.resetRowSelection()}>
            Cancel
          </Button>
          <TagEndpointsButton selectedRows={selectedRows} />
          <DeleteEndpointButton selectedRows={selectedRows} />
        </SelectionBar>
      )}
      <TagOverlay
        resetTable={() => {
          table.resetRowSelection();
        }}
      />
    </div>
  );
}

function DeleteEndpointButton({ selectedRows }: { selectedRows: Row<Host>[] }) {
  const setDeleteEndpointModal = useDeleteEndpointModal(
    (state) => state.setValue,
  );

  return (
    <>
      <Button
        intent="negative"
        icon={<TrashcanIcon />}
        onClick={() => setDeleteEndpointModal(true)}
      >
        Delete endpoints
      </Button>
      <DeleteEndpointModal
        endpointIds={selectedRows.map((row) => row.original.endpoint_id)}
      />
    </>
  );
}
function TagEndpointsButton({ selectedRows }: { selectedRows: Row<Host>[] }) {
  const setTagOverlayProps = useTagOverlayProps((state) => state.setValue);
  const { data: tags } = useEndpointTagsQuery();

  function handleTagOverlayProps(tags: string[], selectedRows: Row<Host>[]) {
    const endpoints = selectedRows.map((r) => r.original.endpoint_id);
    const relevantTags = [
      ...new Set(selectedRows.flatMap((r) => r.original.tags ?? [])),
    ];

    const rowTags = selectedRows.map((r) => r.original.tags);
    const sharedTags =
      rowTags.reduce((acc, current) => {
        return acc?.filter((t) => current?.includes(t));
      }, rowTags[0] ?? []) ?? [];

    setTagOverlayProps({
      endpoints,
      tags,
      relevantTags,
      sharedTags,
    });
  }

  return (
    <Button
      intent="secondary"
      icon={<TagIcon />}
      onClick={() => handleTagOverlayProps(tags ?? [], selectedRows)}
    >
      Tag endpoints
    </Button>
  );
}

function RecommendationView() {
  const { query } = Route.useLoaderDeps();
  const { value, setValue } = useSessionStorage("seeRecommendation", true);

  if (!value) {
    return null;
  }

  return (
    <div className="px-4">
      <Recommendation setSeeRecommendation={setValue} query={query} />
    </div>
  );
}
